import Icon from "features/shared/components/icons/Icon";
import FormGroup from "features/shared/forms/components/FormGroup";
import { UseFormHookResult } from "features/shared/forms/hooks/useForm";
import React, { useEffect } from "react";
import MailMethodEmailSubDialog from "./MailMethodEmailSubDialog";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import CreatableItem from "features/shared/components/creatableItem/CreatableItem";
import { setMailMethodEmailsValidation } from "features/contracting/sections/contractingForms/customerDataForm/customerDataFormValidation";
import { useTranslation } from "react-i18next";

type Props = {
  formHandler: UseFormHookResult;
  disabled?: boolean;
};

const MailMethodEmailsSubForm = ({ formHandler, disabled }: Props) => {
  //  DI
  const dialogService = useInjection<IContentDialogService>(
    IocTypes.ContentDialogService
  );

  //translations
  const tLabels = useTranslation("contracting\\contracting").t;

  // parent form hook props
  const {
    setFormValue,
    getFormValue,
    clearFormValues,
    errors,
    setValidationRule,
    clearValidationErrors,
  } = {
    ...formHandler,
  };

  // side-effects
  useEffect(() => {
    // set default validations
    setMailMethodEmailsValidation(setValidationRule);

    return () => {
      // before unmount component remove values and validations
      setMailMethodEmailsValidation(setValidationRule, "remove");
      clearFormValues(["mailMethodEmails"]);
    };
  }, []);

  // watching email and its settings to mail method emails
  useEffect(() => {
    const emails = getMailMethodEmails();
    const userEmail = getFormValue<string>("email");

    if (emails.length === 0 && userEmail && errors["email"] === undefined) {
      setFormValue("mailMethodEmails", [userEmail]);
    }
  }, [getFormValue<string>("email")]);

  // helpers
  const getMailMethodEmails = (): string[] => {
    const emails = getFormValue<string[]>("mailMethodEmails");

    if (emails !== undefined) {
      return emails;
    }

    return [];
  };

  // handlers
  const submitEmailDialogHandler = (email: string) => {
    let emails: string[] = [...getMailMethodEmails()];
    emails.push(email);

    setFormValue("mailMethodEmails", emails);
    clearValidationErrors(["mailMethodEmails"]);
    dialogService.closeDialog();
  };

  const deleteEmailHandler = (email: string) => {
    let emails: string[] = getMailMethodEmails().filter((x) => x !== email);
    setFormValue("mailMethodEmails", emails);
  };

  const openEmailDialogHandler = () => {
    dialogService.openDialog(
      tLabels("mailMethodEmailsSubForm.dialogTitle"),
      renderEmailSubDialog
    );
  };

  // sub-renders
  const renderEmails = () => {
    const emails = getMailMethodEmails();

    if (emails.length > 0) {
      return emails.map((email, index) => (
        <div className="col-auto px-1" key={index}>
          <CreatableItem
            text={email}
            code={email}
            onActionClick={disabled ? undefined : deleteEmailHandler}
          />
        </div>
      ));
    } else {
      return (
        <div className="col-auto px-1">
          <CreatableItem
            text={tLabels("mailMethodEmailsSubForm.noEmailAdded")}
            code={"noEmailAdded"}
          />
        </div>
      );
    }
  };

  const renderEmailSubDialog = () => {
    return (
      <MailMethodEmailSubDialog
        onSubmit={submitEmailDialogHandler}
        mailMethodEmails={getMailMethodEmails()}
      />
    );
  };

  // render
  return (
    <div className="row mx-n2">
      <div className="col-md-auto px-2">
        <FormGroup
          labelText={tLabels("mailMethodEmailsSubForm.mailMethodEmails.label")}
          validationError={errors["mailMethodEmails"]}
        >
          <div className="row mx-n1">
            {renderEmails()}
            <div className="col-auto px-1">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={openEmailDialogHandler}
                disabled={disabled}
              >
                <Icon icon="plus" />
              </button>
            </div>
          </div>
        </FormGroup>
      </div>
    </div>
  );
};

export default MailMethodEmailsSubForm;
